import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiBarChart2,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiEye,
  FiFileText,
  FiLogIn,
  FiLogOut,
  FiMoon,
  FiSearch,
  FiSun,
  FiX,
} from 'react-icons/fi';
import { useNdTheme } from '../utils/ndTheme';
import { getApiBaseURL } from '../utils/apiUrl';
import { toCyrillicFio } from '../utils/latinToCyrillicFio';
import {
  buildAppUsersAvatarLookupMap,
  avatarSrcForImg,
  resolveAvatarFromUserMap,
} from '../utils/reportAvatars';
import '../styles/newDesign.css';

const REPORT_FETCH_TIMEOUT_MS = 45000;
const AVATAR_COLORS = ['#2dd4bf', '#38bdf8', '#a78bfa', '#fbbf24', '#f0566a', '#34d399', '#f472b6', '#f97316'];

const api = (path) => `${getApiBaseURL()}${path.startsWith('/') ? '' : '/'}${path}`;

function authHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fetchWithTimeout(url, options = {}, timeoutMs = REPORT_FETCH_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  return fetch(url, { ...options, signal: controller.signal }).then(
    (r) => { clearTimeout(timeout); return r; },
    (e) => { clearTimeout(timeout); throw e; }
  );
}

function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getYesterdayDate() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return toLocalDateStr(yesterday);
}

function formatYmd(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-');
  return `${d}.${m}.${y}`;
}

function formatTime(dtStr) {
  if (!dtStr) return '—';
  const match = String(dtStr).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const d = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    if (!Number.isNaN(d.getTime())) {
      return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
    }
  }
  const d = new Date(dtStr);
  if (Number.isNaN(d.getTime())) return dtStr;
  return `${d.toLocaleDateString('ru-RU')}, ${d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}`;
}

function formatClock(dtStr) {
  if (!dtStr) return '—';
  const match = String(dtStr).match(/(\d{2}):(\d{2})/);
  if (match) return `${match[1]}:${match[2]}`;
  const formatted = formatTime(dtStr);
  const timeMatch = formatted.match(/(\d{2}:\d{2})/);
  return timeMatch ? timeMatch[1] : formatted;
}

function parseEventTime(dtStr) {
  if (!dtStr) return null;
  const match = String(dtStr).match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (match) {
    const [, year, month, day, hour, minute, second] = match;
    const d = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    if (!Number.isNaN(d.getTime())) return d;
  }
  const d = new Date(dtStr);
  return Number.isNaN(d.getTime()) ? null : d;
}

function ndInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2);
  return letters.toUpperCase();
}

function ndColorOf(name) {
  const s = String(name || '?');
  let sum = 0;
  for (let i = 0; i < s.length; i += 1) sum += s.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function hoursOf(user) {
  const direct = Number(user?.totalHours);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const str = String(user?.totalTimeStr || '');
  const h = str.match(/(\d+)\s*ч/);
  const m = str.match(/(\d+)\s*мин/);
  return (h ? Number(h[1]) : 0) + (m ? Number(m[1]) : 0) / 60;
}

function workPct(user) {
  return Math.max(0, Math.min(100, (hoursOf(user) / 8) * 100));
}

function displayName(user) {
  return toCyrillicFio(user?.fio || user?.username || user?.technicalUsername || '') || 'Без имени';
}

export default function RemoteWorktimeBoard({ onClose, isMobile = false }) {
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const toastSeq = useRef(0);

  const [currentDate, setCurrentDate] = useState(getYesterdayDate);
  const [startDate, setStartDate] = useState(getYesterdayDate);
  const [endDate, setEndDate] = useState(getYesterdayDate);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reportError, setReportError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEvents, setUserEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAutocomplete, setShowAutocomplete] = useState(false);
  const [sortOrder, setSortOrder] = useState('default');
  const [importLoading, setImportLoading] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [appUsersForAvatars, setAppUsersForAvatars] = useState([]);
  const [toasts, setToasts] = useState([]);

  const pushToast = useCallback((text, tone) => {
    const id = ++toastSeq.current;
    setToasts((prev) => [...prev, { id, text, tone }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3200);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAppUsersForAvatars([]);
      return;
    }
    fetch(api('/api/users'), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        const arr = Array.isArray(d) ? d : (d?.users || []);
        setAppUsersForAvatars(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setAppUsersForAvatars([]));
  }, []);

  const remoteReportAvatarMap = useMemo(
    () => buildAppUsersAvatarLookupMap(appUsersForAvatars),
    [appUsersForAvatars]
  );

  const loadReport = useCallback(async (from = startDate, to = endDate) => {
    if (!from || !to) return;
    setLoading(true);
    setReportError(null);
    try {
      const response = await fetchWithTimeout(api(`/api/remote-worktime-report?start=${from}&end=${to}`), {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setReport(data.report || []);
      } else {
        setReport([]);
        setReportError(data.error || 'Не удалось загрузить отчет');
      }
    } catch (error) {
      setReport([]);
      setReportError(
        error.name === 'AbortError'
          ? 'Загрузка прервана по таймауту (45 сек). Проверьте соединение с сервером.'
          : (error.message || 'Ошибка загрузки')
      );
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (startDate === endDate) setCurrentDate(startDate);
  }, [startDate, endDate]);

  const shiftDay = (delta) => {
    const [y, m, d] = currentDate.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + delta);
    const next = toLocalDateStr(date);
    setCurrentDate(next);
    setStartDate(next);
    setEndDate(next);
    loadReport(next, next);
  };

  const handleNavDateChange = (next) => {
    if (!next) return;
    setCurrentDate(next);
    setStartDate(next);
    setEndDate(next);
    loadReport(next, next);
  };

  const handleImportFromWindowsLog = useCallback(async () => {
    if (!startDate || !endDate) return;
    setImportLoading(true);
    setImportMessage(null);
    try {
      const res = await fetch(api('/api/import-from-windows-log'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ startDate, endDate }),
      });
      const data = await res.json();
      if (data.success) {
        const byId = data.importedByEventId
          ? Object.entries(data.importedByEventId).map(([id, n]) => `${id}:${n}`).join(', ')
          : '';
        const total = data.total ?? 0;
        const already = data.alreadyInDb != null ? ` В базе уже было: ${data.alreadyInDb}.` : '';
        const hint = data.hint ? ` ${data.hint}` : '';
        const text =
          total > 0 && (data.imported || 0) === 0
            ? `В журнале Windows: ${total} событий, новых записей: 0 (все уже импортированы).${already}${hint}`
            : `Загружено из журнала Windows: ${data.imported || 0} записей (в журнале ${total}) за период ${data.period || ''}.${byId ? ` (события: ${byId})` : ''}${already}${hint}`;
        setImportMessage(text);
        pushToast(text, (data.imported || 0) > 0 ? '' : 'nd-amber');
        await loadReport();
        setTimeout(() => setImportMessage(null), 10000);
      } else {
        const hint = data.hint ? ` ${data.hint}` : '';
        const text = (data.error || 'Ошибка импорта. Импорт доступен только когда сервер запущен на Windows.') + hint;
        setImportMessage(text);
        pushToast(text, 'nd-red');
        setTimeout(() => setImportMessage(null), 12000);
      }
    } catch (err) {
      const text = 'Ошибка запроса: ' + (err.message || 'нет связи с сервером');
      setImportMessage(text);
      pushToast(text, 'nd-red');
      setTimeout(() => setImportMessage(null), 6000);
    } finally {
      setImportLoading(false);
    }
  }, [startDate, endDate, loadReport, pushToast]);

  const loadUserEvents = async (username) => {
    if (!startDate || !endDate || !username) return;
    setLoadingEvents(true);
    try {
      const response = await fetch(
        api(`/api/remote-worktime-user-events?username=${encodeURIComponent(username)}&start=${startDate}&end=${endDate}`),
        { headers: authHeaders() }
      );
      const data = await response.json();
      if (data.success) {
        const sortedEvents = (data.events || []).slice().sort((a, b) => {
          const aTime = parseEventTime(a.event_time);
          const bTime = parseEventTime(b.event_time);
          if (!aTime || !bTime) return 0;
          return aTime - bTime;
        });
        setUserEvents(sortedEvents);
      } else {
        setUserEvents([]);
      }
    } catch (error) {
      setUserEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  };

  const userOptions = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const search = searchTerm.toLowerCase().trim();
    const uniqueUsers = Array.from(new Set(report.map((u) => displayName(u))));
    return uniqueUsers.filter((u) => u.toLowerCase().includes(search)).slice(0, 10);
  }, [searchTerm, report]);

  const filteredReport = useMemo(() => {
    if (!searchTerm.trim()) return report;
    const search = searchTerm.toLowerCase().trim();
    return report.filter((user) => {
      const fio = displayName(user).toLowerCase();
      const username = (user.username || '').toLowerCase();
      return fio.includes(search) || username.includes(search);
    });
  }, [report, searchTerm]);

  const sortedReport = useMemo(() => {
    if (sortOrder !== 'alpha') return filteredReport;
    return [...filteredReport].sort((a, b) => displayName(a).localeCompare(displayName(b), 'ru', { sensitivity: 'base' }));
  }, [filteredReport, sortOrder]);

  const stats = useMemo(() => {
    const events = report.reduce((sum, row) => sum + (Number(row.totalEvents) || 0), 0);
    const hours = report.reduce((sum, row) => sum + hoursOf(row), 0);
    return {
      people: report.length,
      events,
      hours: Math.round(hours),
    };
  }, [report]);

  const handleViewUser = async (user) => {
    let username = user.technicalUsername || user.username;
    if (!username || (username === user.fio && /[А-Яа-яЁё]/.test(username))) {
      username = user.fio || user.username;
    }
    if (!username) return;
    setSelectedUser(user);
    await loadUserEvents(username);
  };

  const handleBack = () => {
    setSelectedUser(null);
    setUserEvents([]);
  };

  const handleClose = () => {
    setSelectedUser(null);
    setUserEvents([]);
    onClose?.();
  };

  const avatarFor = (user) => {
    const raw = (user.avatar && String(user.avatar).trim()) || resolveAvatarFromUserMap(user, remoteReportAvatarMap);
    return raw ? avatarSrcForImg(raw) : null;
  };

  const periodLabel = startDate === endDate
    ? formatYmd(startDate)
    : `${formatYmd(startDate)} — ${formatYmd(endDate)}`;

  return (
    <div
      className={`nd-remWrap${isMobile ? ' nd-mobile' : ''}`}
      onClick={isMobile ? undefined : handleClose}
    >
      <div className="nd-remSheet" onClick={(e) => e.stopPropagation()}>
        <div className="nd-sheetHead">
          <div className="nd-sheetIco">🏠</div>
          <h2>
            Отчет Удаленка
            <span>кто работал вне офиса · входы/выходы · отработанное время</span>
          </h2>
          <button
            type="button"
            className="nd-themeBtn"
            onClick={toggleNdTheme}
            title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
          >
            {ndIsLight ? <FiMoon /> : <FiSun />}
          </button>
          {onClose && (
            <button type="button" className="nd-sheetClose" onClick={handleClose} aria-label="Закрыть" title="Закрыть">
              <FiX />
            </button>
          )}
        </div>

        <div className="nd-statChips">
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-green)' }}><i />Сотрудников <b>{stats.people}</b></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-blue)' }}><i />Событий <b>{stats.events}</b></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-orange)' }}><i />Отработано <b>{stats.hours} ч</b></span>
        </div>

        <div>
          <span className="nd-lbl">Быстрая навигация по дням</span>
          <div className="nd-dayNav">
            <button type="button" className="nd-dNavBtn" onClick={() => shiftDay(-1)} aria-label="Предыдущий день">
              <FiChevronLeft />
            </button>
            <input
              className="nd-dateLbl"
              type="date"
              value={currentDate}
              onChange={(e) => handleNavDateChange(e.target.value)}
            />
            <button type="button" className="nd-dNavBtn" onClick={() => shiftDay(1)} aria-label="Следующий день">
              <FiChevronRight />
            </button>
          </div>
        </div>

        <div className="nd-periodP nd-glass">
          <div className="nd-fld">
            <span className="nd-lbl">Начало периода</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                const next = e.target.value;
                setStartDate(next);
                const today = toLocalDateStr(new Date());
                if (next === today) setEndDate(today);
              }}
            />
          </div>
          <div className="nd-fld">
            <span className="nd-lbl">Конец периода</span>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
        </div>

        <div className="nd-remSearch">
          <span className="nd-sIco"><FiSearch size={16} /></span>
          <input
            type="text"
            placeholder="Введите имя сотрудника..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowAutocomplete(true);
            }}
            onFocus={() => setShowAutocomplete(true)}
            onBlur={() => setTimeout(() => setShowAutocomplete(false), 150)}
          />
          {searchTerm && (
            <button type="button" className="nd-clr" onClick={() => { setSearchTerm(''); setShowAutocomplete(false); }} aria-label="Очистить поиск">×</button>
          )}
          {showAutocomplete && userOptions.length > 0 && (
            <div className="nd-remAc">
              {userOptions.map((name) => (
                <button
                  key={name}
                  type="button"
                  onMouseDown={() => {
                    setSearchTerm(name);
                    setShowAutocomplete(false);
                  }}
                >
                  {name}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="nd-remAct">
          <button type="button" className="nd-bigBtn nd-go" onClick={() => loadReport()} disabled={loading}>
            <FiBarChart2 size={16} />
            {loading ? 'Загрузка...' : 'Показать отчет'}
          </button>
          <button
            type="button"
            className="nd-bigBtn nd-win"
            onClick={handleImportFromWindowsLog}
            disabled={importLoading || !startDate || !endDate}
            title="Загрузить события входа/выхода из журнала Windows на сервере за выбранную дату"
          >
            <FiFileText size={16} />
            {importLoading ? 'Импорт...' : 'Подтянуть из журнала Windows'}
          </button>
        </div>
        {importMessage && <div className="nd-remMsg">{importMessage}</div>}

        <div className="nd-sortRow">
          <span className="nd-sl">Сортировка:</span>
          <button type="button" className={`nd-chip${sortOrder === 'default' ? ' nd-on' : ''}`} onClick={() => setSortOrder('default')}>
            По умолчанию
          </button>
          <button type="button" className={`nd-chip${sortOrder === 'alpha' ? ' nd-on' : ''}`} onClick={() => setSortOrder('alpha')}>
            По алфавиту
          </button>
        </div>

        {loading && <div className="nd-remEmpty">Загрузка...</div>}
        {!loading && reportError && (
          <div className="nd-remEmpty">
            <p>{reportError}</p>
            <button type="button" className="nd-btn nd-go" style={{ margin: '12px auto 0' }} onClick={() => loadReport()}>
              Повторить
            </button>
          </div>
        )}
        {!loading && !reportError && sortedReport.length === 0 && (
          <div className="nd-remEmpty">
            {searchTerm ? 'Нет результатов поиска' : 'Нет данных за выбранный период'}
          </div>
        )}

        {!loading && !reportError && sortedReport.length > 0 && (
          <div className="nd-remGrid">
            {sortedReport.map((user, idx) => {
              const name = displayName(user);
              const src = avatarFor(user);
              const color = ndColorOf(name);
              return (
                <div
                  key={user.username || user.technicalUsername || `${name}-${idx}`}
                  className="nd-remCard nd-glass"
                  style={{ animationDelay: `${idx * 40}ms` }}
                >
                  <div className="nd-remTop">
                    <div className="nd-remAv" style={{ '--cc': color }}>
                      {src ? (
                        <img src={src} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : ndInitials(name)}
                    </div>
                    <b>{name}</b>
                    <button type="button" className="nd-remDet" onClick={() => handleViewUser(user)}>
                      <FiEye size={13} />
                      Подробности
                    </button>
                  </div>
                  <div className="nd-remEv">Событий: <b>{user.totalEvents ?? 0}</b></div>
                  <div className="nd-remRow nd-in">
                    <span className="nd-ic"><FiLogIn /></span>
                    Первый вход:
                    <span className="nd-mono">{user.firstLogin ? formatTime(user.firstLogin) : '—'}</span>
                  </div>
                  <div className="nd-remRow nd-out">
                    <span className="nd-ic"><FiLogOut /></span>
                    Последний выход:
                    <span className="nd-mono">{user.lastLogout ? formatTime(user.lastLogout) : '—'}</span>
                  </div>
                  <div className="nd-remRow nd-wrk">
                    <span className="nd-ic"><FiClock /></span>
                    Отработано:
                    <b>{user.totalTimeStr || '0 мин'}</b>
                  </div>
                  <div className="nd-remBar"><i style={{ width: `${workPct(user)}%` }} /></div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedUser && (
        <div
          className="nd-subModal"
          onClick={(e) => {
            e.stopPropagation();
            handleBack();
          }}
        >
          <div className="nd-mBox" onClick={(e) => e.stopPropagation()}>
            <h3>
              <span>{displayName(selectedUser)}</span>
              <button type="button" onClick={handleBack} aria-label="Закрыть">✕</button>
            </h3>
            <div className="nd-mSub">События за {periodLabel}</div>
            <div className="nd-mStats">
              <div className="nd-mStat"><span>Событий</span><b>{selectedUser.totalEvents ?? userEvents.length}</b></div>
              <div className="nd-mStat"><span>Первый вход</span><b>{selectedUser.firstLogin ? formatClock(selectedUser.firstLogin) : '—'}</b></div>
              <div className="nd-mStat"><span>Последний выход</span><b>{selectedUser.lastLogout ? formatClock(selectedUser.lastLogout) : '—'}</b></div>
            </div>
            {loadingEvents && <div className="nd-remEmpty">Загрузка событий...</div>}
            {!loadingEvents && userEvents.length === 0 && (
              <div className="nd-remEmpty">Нет событий за выбранный период</div>
            )}
            {!loadingEvents && userEvents.length > 0 && (
              <div className="nd-tlList">
                {userEvents.map((event, index) => {
                  const isLogin = event.event_type === 'login';
                  return (
                    <div key={`${event.event_time}-${index}`} className="nd-tlRow">
                      <span
                        className="nd-bd"
                        style={{ background: isLogin ? 'var(--nd-green)' : 'var(--nd-red)', boxShadow: `0 0 8px ${isLogin ? 'var(--nd-green)' : 'var(--nd-red)'}` }}
                      />
                      <span className="nd-tm">{formatClock(event.event_time)}</span>
                      <span className="nd-tt">{isLogin ? 'Вход в систему' : 'Выход из системы'}</span>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="nd-mActs">
              <button type="button" className="nd-btn nd-ghost" onClick={handleBack}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {toasts.length > 0 && (
        <div className="nd-toasts">
          {toasts.map((t) => (
            <div key={t.id} className={`nd-toast${t.tone ? ` ${t.tone}` : ''}`}>{t.text}</div>
          ))}
        </div>
      )}
    </div>
  );
}
